import React, { useCallback, useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

/**
 * Password set/change form. Reads /auth/has-password on mount to determine
 * whether to require the current password.
 */
const CustomerPasswordForm = ({ authClient }) => {
  const { toast } = useToast();
  const [hasPassword, setHasPassword] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const loadHasPassword = useCallback(async () => {
    try {
      const r = await authClient.get('/auth/has-password');
      setHasPassword(Boolean(r.data?.has_password));
    } catch (e) { console.warn('Failed to read password status', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadHasPassword(); }, [loadHasPassword]);

  const onChange = async (e) => {
    e?.preventDefault?.();
    if (newPw.length < 8) return toast({ title: 'New password must be at least 8 characters', variant: 'destructive' });
    if (!/[A-Za-z]/.test(newPw) || !/\d/.test(newPw)) return toast({ title: 'New password must contain letters + numbers', variant: 'destructive' });
    if (newPw !== confirmPw) return toast({ title: 'Passwords do not match', variant: 'destructive' });
    if (hasPassword && !currentPw) return toast({ title: 'Enter your current password', variant: 'destructive' });
    setSaving(true);
    try {
      await authClient.post('/auth/set-password', hasPassword ? { current_password: currentPw, new_password: newPw } : { new_password: newPw });
      toast({ title: hasPassword ? 'Password changed' : 'Password set — you can now sign in with password' });
      setCurrentPw(''); setNewPw(''); setConfirmPw(''); setHasPassword(true);
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-6">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-1">
        <Lock className="w-5 h-5 text-[#2c54b2]" /> {hasPassword ? 'Change password' : 'Set a password'}
      </h3>
      <p className="text-xs text-gray-500 mb-4">{hasPassword ? 'Update your account password.' : 'Setting a password lets you skip OTP next time.'}</p>

      <form onSubmit={onChange} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {hasPassword && (
          <div className="md:col-span-2">
            <Label className="text-xs text-gray-700">Current password</Label>
            <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} autoComplete="current-password" className="mt-1" data-testid="profile-current-pw" />
          </div>
        )}
        <div>
          <Label className="text-xs text-gray-700">New password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 8 chars, letters + numbers" autoComplete="new-password" className="mt-1" data-testid="profile-new-pw" />
        </div>
        <div>
          <Label className="text-xs text-gray-700">Confirm new password</Label>
          <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Repeat new password" autoComplete="new-password" className="mt-1" data-testid="profile-confirm-pw" />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" disabled={saving} className="bg-[#2c54b2] hover:bg-[#1f3f87]" data-testid="profile-change-pw-btn">
            <Lock className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : (hasPassword ? 'Change password' : 'Set password')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerPasswordForm;
